/**
 * El paso 3: la revision. Es la pantalla que evita el desastre.
 *
 * QUE TIENE QUE CONTESTAR CADA FILA, DE UN VISTAZO
 * -----------------------------------------------
 *   1. ¿Con que producto matcheo, y COMO? Un match por codigo de proveedor y uno
 *      por parecido de nombre no valen lo mismo, y la diferencia no se puede
 *      dejar implicita en un numerito de confianza.
 *   2. ¿Cuanto costaba y cuanto va a costar?
 *   3. ¿Cuanto se mueve, en porcentaje? Es la senal que delata un error de
 *      lectura del PDF mucho antes que el costo absoluto.
 *   4. ¿Que precio de venta queda? Es lo unico que ve el cliente. La vista
 *      `v_lista_revision` ya lo calcula con el markup y el redondeo del
 *      producto, asi que no se recalcula nada aca.
 *   5. ¿Que va a pasar con ella? ACTUALIZAR, CREAR o IGNORAR, cambiable.
 *
 * LO QUE ESTA PANTALLA NO HACE
 * ----------------------------
 * No "arregla" las propuestas del matching. `matchear_lista()` deja los matches
 * por nombre en IGNORAR a proposito: nadie verifico que sean el mismo producto.
 * Pasarlos a ACTUALIZAR por comodidad convertiria el paso de revision en un
 * tramite y devolveria el sistema al lugar donde un import escribe 400 costos
 * sin que nadie los haya mirado.
 */
import type { ReactNode } from "react";
import { ArrowDown, ArrowRight, ArrowUp, Pencil, TriangleAlert } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { Segmented, type OpcionSegmented } from "@/components/ui/segmented";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { aNumero } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import {
  ACCIONES,
  TEXTO_MATCH,
  cambiaLaMoneda,
  confianzaDe,
  formatearVariacion,
  opcionesDeAccion,
  riesgoDeFila,
  validarAccion,
  variacionDe,
  type AccionFila,
  type FilaRevision,
} from "./logica";

export interface TablaRevisionProps {
  filas: ReadonlyArray<FilaRevision>;
  /** El import ya se aplico: todo queda en solo lectura hasta que se revierta. */
  bloqueada: boolean;
  /** Fila con un PATCH en vuelo, para no dejar apretar dos veces. */
  guardandoId: number | null;
  onAccion: (filaId: number, accion: AccionFila) => void;
  onElegirProducto: (fila: FilaRevision) => void;
  mensajeVacio: string;
  /** Todavia no llegaron las filas: se muestra `Cargando`, nunca el vacio. */
  cargando?: boolean;
  /** "Limpiar filtros" cuando el vacio es por el filtro y no por la lista. */
  accionVacio?: ReactNode;
}

const ETIQUETA_ACCION: Record<AccionFila, string> = {
  ACTUALIZAR: "Actualizar",
  CREAR: "Crear",
  IGNORAR: "Ignorar",
};

/**
 * Cinco columnas y no siete. «Cómo matcheó» va debajo del producto y la
 * variación debajo del costo: con siete, a 1400 px y el panel de impacto al
 * lado, la tabla medía 1256 px en 802 visibles, y «Al aplicar» —la columna
 * que se toca— quedaba detrás del scroll horizontal.
 */
const COLUMNAS = 5;

export function TablaRevision(props: TablaRevisionProps): JSX.Element {
  const { filas, mensajeVacio, cargando = false, accionVacio } = props;

  return (
    <>
      {/* Desktop: tabla con encabezado pegado arriba. */}
      <Card padding="none" className="hidden overflow-hidden lg:block">
        <Table densidad="compacta" alto="acotado">
          <TableHeader>
            <TableRow>
              <TableHead>Fila de la lista</TableHead>
              <TableHead>Producto y cómo matcheó</TableHead>
              <TableHead className="text-right">Costo y variación</TableHead>
              <TableHead className="text-right">Precio de venta</TableHead>
              <TableHead>Al aplicar</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.length === 0 ? (
              cargando ? (
                <tr>
                  <td colSpan={COLUMNAS}>
                    <Cargando texto="Trayendo las filas…" />
                  </td>
                </tr>
              ) : (
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} accion={accionVacio} />
              )
            ) : (
              filas.map((fila) => <Fila key={fila.id} fila={fila} {...props} />)
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: una tarjeta por fila. Sin scroll horizontal. */}
      <div className="flex flex-col gap-2 lg:hidden">
        {filas.length === 0 ? (
          cargando ? (
            <Cargando texto="Trayendo las filas…" />
          ) : (
            <EstadoVacio titulo={mensajeVacio} accion={accionVacio} />
          )
        ) : (
          filas.map((fila) => <Tarjeta key={fila.id} fila={fila} {...props} />)
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fila de tabla
// ─────────────────────────────────────────────────────────────────────────────

function Fila({
  fila,
  bloqueada,
  guardandoId,
  onAccion,
  onElegirProducto,
}: TablaRevisionProps & { fila: FilaRevision }): JSX.Element {
  const riesgo = riesgoDeFila(fila);
  const validacion = validarAccion(fila, fila.accion);

  return (
    <TableRow className={cn("border-l-2", BORDE_RIESGO[riesgo])}>
      <TableCell className="max-w-[16rem]">
        <span className="block truncate text-sm">{fila.descripcion}</span>
        <span className="block truncate text-xs tabular-nums text-muted-foreground">
          {fila.codigo_externo ?? "sin código"}
        </span>
      </TableCell>

      <TableCell className="max-w-[16rem]">
        <ProductoDeFila
          fila={fila}
          bloqueada={bloqueada}
          onElegirProducto={() => onElegirProducto(fila)}
        />
        <div className="mt-1">
          <SelloMatch fila={fila} />
        </div>
      </TableCell>

      <TableCellImporte>
        <span className="inline-flex flex-col items-end gap-1">
          <CostoDeFila fila={fila} />
          <VariacionDeFila fila={fila} />
        </span>
      </TableCellImporte>

      <TableCellImporte>
        <PrecioDeFila fila={fila} />
      </TableCellImporte>

      <TableCell>
        <SelectorAccion
          fila={fila}
          bloqueada={bloqueada}
          guardando={guardandoId === fila.id}
          onAccion={(a) => onAccion(fila.id, a)}
        />
        {validacion.motivo ? (
          <p className="mt-1 max-w-[14rem] text-xs text-destructive">{validacion.motivo}</p>
        ) : null}
        {fila.error ? (
          <p className="mt-1 max-w-[14rem] text-xs text-destructive">{fila.error}</p>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

const BORDE_RIESGO: Record<string, string> = {
  GRAVE: "border-l-destructive",
  ALTO: "border-l-warning",
  MEDIO: "border-l-border",
  NINGUNO: "border-l-transparent",
};

// ─────────────────────────────────────────────────────────────────────────────
// Tarjeta de mobile
// ─────────────────────────────────────────────────────────────────────────────

function Tarjeta({
  fila,
  bloqueada,
  guardandoId,
  onAccion,
  onElegirProducto,
}: TablaRevisionProps & { fila: FilaRevision }): JSX.Element {
  const validacion = validarAccion(fila, fila.accion);

  // Tarjeta-registro (06 §3.4): el borde lateral es el de la MONEDA del costo;
  // el riesgo lo dicen la variacion (flecha, triangulo) y el badge del match.
  return (
    <Card as="article" padding="compacto" acento={fila.moneda} className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{fila.descripcion}</p>
          <p className="truncate text-xs tabular-nums text-muted-foreground">
            {fila.codigo_externo ?? "sin código"}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <CostoDeFila fila={fila} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SelloMatch fila={fila} />
        <VariacionDeFila fila={fila} />
      </div>

      <ProductoDeFila
        fila={fila}
        bloqueada={bloqueada}
        onElegirProducto={() => onElegirProducto(fila)}
      />

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-xs text-muted-foreground">Precio de venta</span>
        <PrecioDeFila fila={fila} />
      </div>

      <div className="space-y-1">
        <SelectorAccion
          fila={fila}
          bloqueada={bloqueada}
          guardando={guardandoId === fila.id}
          onAccion={(a) => onAccion(fila.id, a)}
        />
        {validacion.motivo ? <p className="text-xs text-destructive">{validacion.motivo}</p> : null}
        {validacion.advertencia ? (
          <p className="text-xs text-warning">{validacion.advertencia}</p>
        ) : null}
        {fila.error ? <p className="text-xs text-destructive">{fila.error}</p> : null}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Piezas
// ─────────────────────────────────────────────────────────────────────────────

function ProductoDeFila({
  fila,
  bloqueada,
  onElegirProducto,
}: {
  fila: FilaRevision;
  bloqueada: boolean;
  onElegirProducto: () => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="min-w-0">
        {fila.producto_id === null ? (
          <span className="text-sm text-muted-foreground">Ninguno</span>
        ) : (
          <>
            <span className="block truncate text-sm">{fila.producto_nombre ?? "—"}</span>
            <span className="block truncate text-xs tabular-nums text-muted-foreground">
              {fila.codigo_interno ?? "—"}
            </span>
          </>
        )}
      </div>
      {!bloqueada ? (
        <IconButton
          icono={Pencil}
          tamano="sm"
          onClick={onElegirProducto}
          className="shrink-0 text-muted-foreground"
          aria-label={`Elegir el producto de «${fila.descripcion}»`}
        />
      ) : null}
    </div>
  );
}

/**
 * COMO matcheo, no cuanto.
 *
 * La confianza sola engana: 0.90 suena altisimo y sin embargo significa "el
 * codigo coincide, pero con el de OTRO proveedor". El tipo de match va primero y
 * en palabras; el numero va al lado, chico, para desempatar entre dos matches
 * por nombre.
 */
function SelloMatch({ fila }: { fila: FilaRevision }): JSX.Element {
  const tipo = fila.match_tipo ?? "SIN_MATCH";
  const texto = TEXTO_MATCH[tipo];
  const confianza = confianzaDe(fila);

  const variante =
    tipo === "CODIGO_EXTERNO" || tipo === "CODIGO_INTERNO" || tipo === "MANUAL"
      ? "secondary"
      : tipo === "NOMBRE"
        ? "warning"
        : "outline";

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" title={texto.largo}>
      <Badge variant={variante} className="whitespace-nowrap">
        {texto.corto}
      </Badge>
      {confianza !== null ? (
        <span className="text-xs tabular-nums text-muted-foreground">
          {Math.round(confianza * 100)} %
        </span>
      ) : null}
    </span>
  );
}

function CostoDeFila({ fila }: { fila: FilaRevision }): JSX.Element {
  const anterior = fila.costo_anterior === null ? null : aNumero(fila.costo_anterior);

  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <Importe monto={aNumero(fila.costo)} moneda={fila.moneda} tamano="sm" />
      {anterior !== null && fila.moneda_anterior !== null ? (
        // El chip del anterior solo cuando la moneda cambia: ahi es la unica
        // forma de ver de un vistazo que no se comparan peras con manzanas.
        <span className="inline-flex items-baseline gap-1 text-xs text-muted-foreground">
          antes
          <Importe
            monto={anterior}
            moneda={fila.moneda_anterior}
            tamano="sm"
            codigo={cambiaLaMoneda(fila)}
          />
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">sin costo anterior</span>
      )}
    </span>
  );
}

/**
 * La variacion, con flecha ademas de color.
 *
 * Un costo que sube va en rojo porque es lo que hay que mirar: la lista nueva
 * encarece. La flecha existe para que la lectura no dependa del color, igual que
 * el chip de moneda en los importes.
 */
function VariacionDeFila({ fila }: { fila: FilaRevision }): JSX.Element {
  if (cambiaLaMoneda(fila)) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-destructive">
        <TriangleAlert className="h-3 w-3" aria-hidden="true" />
        cambia de {fila.moneda_anterior} a {fila.moneda}
      </span>
    );
  }

  const variacion = variacionDe(fila);
  if (variacion === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const riesgo = riesgoDeFila(fila);
  const sube = variacion > 0;
  const Flecha = variacion === 0 ? ArrowRight : sube ? ArrowUp : ArrowDown;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium tabular-nums",
        variacion === 0 && "text-muted-foreground",
        variacion > 0 && "text-destructive",
        variacion < 0 && "text-success",
      )}
    >
      {riesgo === "GRAVE" ? <TriangleAlert className="h-3 w-3" aria-hidden="true" /> : null}
      <Flecha className="h-3 w-3" aria-hidden="true" />
      {formatearVariacion(variacion)}
    </span>
  );
}

/**
 * El precio de venta que queda si se aplica la fila.
 *
 * Sale de `v_lista_revision`, que llama a `calcular_precio()` con el markup, el
 * redondeo y la cotizacion vigentes. Es el unico numero de esta pantalla que el
 * cliente del local va a ver, y por eso esta antes de aplicar y no despues.
 */
function PrecioDeFila({ fila }: { fila: FilaRevision }): JSX.Element {
  const moneda = fila.precio_moneda;
  const actual = fila.precio_actual === null ? null : aNumero(fila.precio_actual);
  const resultante = fila.precio_resultante === null ? null : aNumero(fila.precio_resultante);

  if (moneda === null || resultante === null) {
    return <span className="text-xs text-muted-foreground">se define al crearlo</span>;
  }

  // Una fila ignorada no toca el producto: se ve el precio de hoy, sin tachar
  // nada. Antes se dibujaba el cambio como si se fuera a aplicar.
  if (fila.accion === "IGNORAR") {
    return actual === null ? (
      <span className="text-xs text-muted-foreground">—</span>
    ) : (
      <Importe monto={actual} moneda={moneda} tamano="sm" />
    );
  }

  // Ya aplicada, el producto tiene el precio nuevo y compararlo contra si
  // mismo decia «no se mueve» en lo que si se movio. Se muestra el que quedo.
  if (fila.aplicada) {
    return <Importe monto={resultante} moneda={moneda} tamano="sm" />;
  }

  const sube = actual !== null && resultante > actual;
  const baja = actual !== null && resultante < actual;

  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span className="inline-flex items-center gap-1">
        {sube || baja ? (
          <Importe
            monto={actual ?? 0}
            moneda={moneda}
            tamano="sm"
            codigo={false}
            className="line-through"
          />
        ) : null}
        <Importe monto={resultante} moneda={moneda} tamano="sm" />
      </span>
      {!sube && !baja && actual !== null ? (
        <span className="text-xs text-muted-foreground">no se mueve</span>
      ) : null}
    </span>
  );
}

/**
 * Los tres destinos posibles de la fila.
 *
 * Tres botones y no un desplegable: en una tabla de 400 filas, cambiar una
 * accion tiene que ser un click, no tres. El que no se puede elegir queda
 * deshabilitado con el motivo en el `title`, en vez de dejar apretar y contestar
 * con un 422 del backend.
 */
function SelectorAccion({
  fila,
  bloqueada,
  guardando,
  onAccion,
}: {
  fila: FilaRevision;
  bloqueada: boolean;
  guardando: boolean;
  onAccion: (accion: AccionFila) => void;
}): JSX.Element {
  const opciones = opcionesDeAccion(fila);

  const segmentos: OpcionSegmented<AccionFila>[] = ACCIONES.map((accion) => {
    const opcion = opciones.find((o) => o.accion === accion);
    const habilitada = opcion?.validacion.valida !== false;
    const activa = fila.accion === accion;
    const title = opcion?.validacion.motivo ?? opcion?.validacion.advertencia;
    return {
      valor: accion,
      etiqueta: ETIQUETA_ACCION[accion],
      disabled: !habilitada && !activa,
      ...(title ? { title } : {}),
    };
  });

  return (
    <Segmented
      apariencia="segmentado"
      tamano="sm"
      etiqueta={`Qué hacer con «${fila.descripcion}»`}
      opciones={segmentos}
      valor={fila.accion}
      onChange={onAccion}
      disabled={bloqueada || guardando}
    />
  );
}
